// @skade/contracts — GNÅ↔SKADE identitets-lenke + utgående (toveis) meldinger.
//
// MODELL (valgt med bruker): hver SKADE-rolle-konto ER OGSÅ en unik GNÅ/Matrix-
// bruker (@skade_<rolle>:matrix.follodefence.com). En operatør kan i tillegg
// LENKE sin private GNÅ-bruker (personen, f.eks. «Kaptein Dan …») til rollen, og
// «swappe»: agere som Sjef i SKADE, men være seg selv i GNÅ. Rolle-Matrix-kontoen
// er det 1:1/1:flere fra SKADE går UT som, så GNÅ-brukeren ser rollen som avsender.
//
// Ren, testbar logikk: id-utledning, validering, wire for utgående meldinger.
// Selve Matrix-sendingen gjøres i bro-tjenesten (untestbar mot ekte Matrix).
import { z } from 'zod';
/** Standard hjemtjener-domene for GNÅ (kan overstyres per miljø). */
export const GNA_HOMESERVER = 'matrix.follodefence.com';
/** Ved GENERERING holder vi localpart enkel: a-z 0-9 . _ - (unngår / og =,
 * som Matrix tillater, men som gir stygge id-er). Alt annet → «-». */
const LOCALPART_OK = /[^a-z0-9._-]+/g;
/** SKADE-bruker-id → Matrix-localpart (saniterert, «skade_»-prefiks). */
export function skadeMatrixLocalpart(skadeUserId) {
    const base = skadeUserId.replace(/^@/, '').replace(/:.*$/, '').toLowerCase();
    const clean = base.replace(LOCALPART_OK, '-').replace(/^-+|-+$/g, '') || 'bruker';
    return `skade_${clean}`;
}
/** SKADE-bruker-id → full Matrix-id på hjemtjeneren. */
export function skadeMatrixUserId(skadeUserId, homeserver = GNA_HOMESERVER) {
    return `@${skadeMatrixLocalpart(skadeUserId)}:${homeserver}`;
}
/**
 * GNÅ-Matrix-id → SKADE-bruker-id (motsatt vei av `skadeMatrixLocalpart`).
 *
 * HVORFOR DETTE MÅ UTLEDES OG IKKE VELGES: ved innløsning av en enrolment-nøkkel
 * kunne brukeren tidligere finne på sin egen SKADE-id (`/auth/enroll` tok
 * `userId` fra klienten). Da er ingen senere revisjonslinje attribuerbar til en
 * person, og to kontoer for samme menneske kan ikke oppdages. Id-en utledes nå
 * av GNÅ-identiteten nøkkelen ble utstedt TIL, så den er deterministisk og
 * sporbar: «@gna4791994407:matrix.follodefence.com» → «gna4791994407».
 *
 * Tom streng for noe som ikke er en Matrix-id (kalleren skal avvise da).
 */
export function skadeUserIdFraGnaMatrixId(matrixId) {
    if (!isMatrixUserId(matrixId))
        return '';
    return matrixId.replace(/^@/, '').replace(/:.*$/, '').toLowerCase();
}
/** Er dette en SKADE-eid Matrix-identitet (@skade_*)? */
export function isSkadeMatrixId(matrixId) {
    return /^@skade_/.test(matrixId);
}
/** Gyldig Matrix bruker-id? «@localpart:domene». */
export function isMatrixUserId(id) {
    return /^@[a-z0-9._=/-]+:[^:\s]+$/i.test(id);
}
/** Gyldig Matrix rom-id? «!opaque:domene». */
export function isMatrixRoomId(id) {
    return /^![^:\s]+:[^:\s]+$/.test(id);
}
// ── Identitets-lenke (rolle-konto ↔ privat GNÅ-bruker) ──────────────────────
export const SkadeLinkSchema = z.object({
    /** SKADE-hub-bruker-id (rollen), f.eks. «HV-11-203». */
    skadeUserId: z.string().max(64),
    /** Rollens Matrix-id på GNÅ-hjemtjeneren (@skade_…). */
    matrixUserId: z.string().max(255),
    /** Personens PRIVATE GNÅ-bruker (for «swap»), f.eks. «@dan.isdahl-engh:…». */
    gnaPrivateUser: z.string().max(255).optional(),
    /** Visningsnavn for rollen i GNÅ (det GNÅ-brukeren ser som avsender). */
    displayName: z.string().max(200).optional(),
});
/** Bygg en standard lenke for en SKADE-bruker (rolle → Matrix-id). */
export function makeSkadeLink(skadeUserId, opts = {}) {
    return SkadeLinkSchema.parse({
        skadeUserId,
        matrixUserId: skadeMatrixUserId(skadeUserId, opts.homeserver),
        gnaPrivateUser: opts.gnaPrivateUser,
        displayName: opts.displayName,
    });
}
// ── Utgående melding (SKADE → GNÅ), toveis ──────────────────────────────────
/** En utgående melding fra SKADE til GNÅ: enten 1:1 (DM til en GNÅ-bruker) eller
 * 1:flere (inn i et eksisterende Matrix-rom). Bro-tjenesten oppretter DM-rom ved
 * behov og sender som rollens Matrix-konto. */
export const GnaOutboundSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('dm'),
        id: z.string().max(64),
        /** Mottakerens GNÅ/Matrix bruker-id (@bruker:domene). */
        to: z.string().max(255).refine(isMatrixUserId, 'ugyldig Matrix bruker-id'),
        text: z.string().min(1).max(8000),
        tsMs: z.number(),
        /** Stemples av hubben = rollens Matrix-id (aldri klientoppgitt). */
        fromMatrix: z.string().max(255).default(''),
        replyTo: z.string().max(255).optional(),
    }),
    z.object({
        kind: z.literal('room'),
        id: z.string().max(64),
        /** Mål-rom-id (!rom:domene). */
        to: z.string().max(255).refine(isMatrixRoomId, 'ugyldig Matrix rom-id'),
        text: z.string().min(1).max(8000),
        tsMs: z.number(),
        fromMatrix: z.string().max(255).default(''),
        replyTo: z.string().max(255).optional(),
    }),
]);
/** Valider en utgående melding (defensiv, null ved ugyldig). */
export function parseGnaOutbound(raw) {
    const p = GnaOutboundSchema.safeParse(raw);
    return p.success ? p.data : null;
}
/** Stemple avsender = rollens autentiserte Matrix-id (kan ikke forfalskes). */
export function stampOutboundFrom(msg, fromMatrix) {
    return { ...msg, fromMatrix };
}
// ── Innkommende svar (GNÅ → SKADE), leveranse 2 ─────────────────────────────
/** En GNÅ-brukers svar i en 1:1-samtale, adressert til en SKADE-rolle-konto.
 * Broen leser rollens DM-rom og POSTer dette til hubben, som ruter til RIKTIG
 * SKADE-operatør (den hvis @skade_-id matcher `toRole`). */
export const GnaInboundDmSchema = z.object({
    id: z.string().max(128),
    /** GNÅ-brukeren som sendte (autentisert Matrix-avsender). */
    from: z.string().max(255).refine(isMatrixUserId, 'ugyldig avsender'),
    /** Rollens Matrix-id meldingen kom til (@skade_…). Ruter til operatøren. */
    toRole: z.string().max(255).refine(isMatrixUserId, 'ugyldig mottaker-rolle'),
    text: z.string().max(8000).default(''),
    tsMs: z.number(),
    roomId: z.string().max(255).optional(),
});
export function parseGnaInboundDm(raw) {
    const p = GnaInboundDmSchema.safeParse(raw);
    return p.success ? p.data : null;
}
//# sourceMappingURL=gna-link.js.map